import type { BatchAmountUpdate, LoadRow } from "./api";
import {
  centsToDollars,
  parseCents,
  type AmountTicket,
  type ResolvedAmount,
} from "./amounts";
import { formatLoadDate, formatMoveType } from "./format";

// Bulk capture: the pure half. The screen owns the pickers and the
// progress copy; everything decidable without a DOM lives here so it can
// be unit-tested headlessly (same split as lib/selectors.ts).

// Extraction runs synchronously on the request path — ~1-3s per ticket
// since slice 8 moved it to a vision model, down from Tesseract's 5-25s.
// Two at a time still earns its keep: it halves a batch's wall time while
// keeping a driver's eight-photo upload from opening eight simultaneous
// requests against a small container (and eight priced model calls at
// once). Raise it only with an eye on the provider's rate limit.
export const CAPTURE_CONCURRENCY = 2;

// Run `fn` over `items` with at most `limit` in flight, preserving input
// order in the result. Rejections are NOT caught here — the caller wraps
// per-item work so one bad ticket becomes a failed row rather than a
// failed batch.
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;

  async function worker(): Promise<void> {
    // Each worker pulls the next index until the queue is drained, so a
    // slow ticket holds up only its own worker.
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]!, i);
    }
  }

  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    () => worker(),
  );
  await Promise.all(workers);
  return results;
}

// What one ticket's upload produced. Exactly one of load/error is set —
// a per-ticket failure (not an image, OCR blew up) flags its own row and
// leaves the rest of the batch alone.
export interface BatchCaptureResult {
  fileName: string;
  load: LoadRow | null;
  error: string | null;
}

export interface BatchReviewRow {
  // Stable per-row identity for React keys and test ids. The load id
  // once we have one; otherwise the file's position, since a failed
  // upload has no id and two photos can share a name.
  key: string;
  loadId: number | null;
  fileName: string;
  containerLabel: string;
  // "Full out · Aug 15", skipping parts the parser didn't get so a row
  // never renders a dangling separator.
  metaLabel: string;
  // The facts lib/amounts.ts resolves against. Kept on the row so the
  // screen has one object per ticket rather than parallel arrays.
  moveType: string | null;
  containerNo: string | null;
  // The amount already on the load in cents — what the vision model read
  // off the ticket at capture. Null when it found no figure, which is
  // what makes the row fall through to a split or the flat rate.
  handwrittenCents: number | null;
  // True when that amount came from the parser (rendered as "auto").
  fromOcr: boolean;
  failed: boolean;
  errorMessage: string | null;
}

// Did the parser price this load, or did a human? Capture writes
// field_provenance.amount = { source: "ocr" }; a later PATCH flips it to
// "human". Anything else (including a null blob) is not an OCR prefill.
function isOcrPriced(load: LoadRow): boolean {
  return load.fieldProvenance?.amount?.source === "ocr";
}

export function buildBatchReviewRows(results: BatchCaptureResult[]): BatchReviewRow[] {
  return results.map((r, i) => {
    if (!r.load) {
      return {
        key: `failed-${i}`,
        loadId: null,
        fileName: r.fileName,
        containerLabel: r.fileName,
        metaLabel: "",
        moveType: null,
        containerNo: null,
        // A failed row has nothing to price — it is a re-shoot, not a
        // to-do on the amount count, so it must not inflate that number
        // and must never be auto-paired with anything.
        handwrittenCents: null,
        fromOcr: false,
        failed: true,
        errorMessage: r.error ?? "Capture failed",
      };
    }

    const load = r.load;
    const meta = [formatMoveType(load.moveType), formatLoadDate(load.loadDate)].filter(
      (part): part is string => part !== null && part !== "—",
    );
    const handwrittenCents = parseCents(load.amount);

    return {
      key: `load-${load.id}`,
      loadId: load.id,
      fileName: r.fileName,
      containerLabel: load.containerNo ?? "—",
      metaLabel: meta.join(" · "),
      moveType: load.moveType,
      containerNo: load.containerNo,
      handwrittenCents,
      fromOcr: handwrittenCents !== null && isOcrPriced(load),
      failed: false,
      errorMessage: null,
    };
  });
}

// The rows the amount resolver operates on — captured rows only. A failed
// upload has no load to price and no container to pair on, so it is
// dropped here rather than filtered at every call site.
export function toAmountTickets(rows: BatchReviewRow[]): AmountTicket[] {
  return rows
    .filter((row) => !row.failed && row.loadId !== null)
    .map((row) => ({
      id: row.key,
      moveType: row.moveType,
      containerNo: row.containerNo,
      handwrittenCents: row.handwrittenCents,
    }));
}

// Which rows Confirm All actually writes, and what provenance each one
// carries. Every priced row is written — a flat-rate fill and a split leg
// are both real decisions this batch made, and the load row is where they
// have to land.
//
// Two rows are skipped. One with no resolved amount (no handwriting, no
// rate set) has nothing to write and stays flagged. One the model priced
// that nothing has changed is left alone: re-PATCHing it would spend a
// request to rewrite the same number, and its provenance already says the
// parser read it.
export function collectAmountUpdates(
  rows: BatchReviewRow[],
  resolved: ResolvedAmount[],
): BatchAmountUpdate[] {
  const byKey = new Map(resolved.map((r) => [r.ticketId, r]));
  const updates: BatchAmountUpdate[] = [];

  for (const row of rows) {
    if (row.failed || row.loadId === null) continue;
    const r = byKey.get(row.key);
    if (!r || r.amountCents === null) continue;
    if (r.source === "handwritten" && r.amountCents === row.handwrittenCents) continue;

    updates.push({
      id: row.loadId,
      amount: centsToDollars(r.amountCents),
      amountSource: r.source,
    });
  }
  return updates;
}

// How many rows still have no usable amount — the driver's actual to-do.
// Counted off the RESOLVED amounts, so typing a default rate clears the
// count for every blank row at once.
export function countNeedingAmount(resolved: ResolvedAmount[]): number {
  return resolved.filter((r) => r.amountCents === null).length;
}
